






































import { defineComponent, ref, computed, useContext } from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { useTranslations, useKECart } from '@vue-storefront/novulo';
import useGoogleAnalytics, { eventBuilder } from '~/hooks/useGoogleAnalytics';
/* @ts-ignore */
import carePlanImage from '@/assets/images/careplan-logo-new.png';
import { addCurrency, priceFormat, getCurrencyCode, getBrowserDetails, calculateInsurancePrice } from '~/helpers/utils';

export default defineComponent({
  name: 'MiniCartDrawerCareplan',
  props: {
    product: { type: Object, required: true },
    cartItemId: { type: Number, default: null },
    cartItemIsInsured: { type: Boolean, default: null }
  },
  setup(props) {
    const { translate } = useTranslations();
    const vsfContext = useVSFContext();
    const context = useContext();
    const { cartItems, toggleInsurance } = useKECart();
    const { registerEvent } = useGoogleAnalytics();

    const isLoading = ref(false);
    const optimistic = ref<boolean | null>(null);

    const isInsured = computed<boolean>(() => {
      if (props.cartItemIsInsured !== null) return Boolean(props.cartItemIsInsured);

      return Boolean(props.product?.insuranceData?.isInsured);
    });

    const uiInsuranceState = computed<boolean>(() => {
      return isLoading.value && optimistic.value !== null
        ? Boolean(optimistic.value)
        : isInsured.value;
    });

    const wt = (vsfContext as any).$novulo?.config?.state?.getWt?.() ?? null;
    const isBE = computed(() => [40, 43, 7, 20, 53].includes(wt));
    const isNL = computed(() => [11, 51].includes(wt));
    const isDE = computed(() => [21, 28, 30, 35, 57, 60, 61].includes(wt));
    const isLU = computed(() => [24, 25].includes(wt));

    const descriptionKey = computed(() =>
      isBE.value ? 'careplan_description_be' : 'careplan_description_nl'
    );

    const insurancePrice = computed<number>(() =>
      calculateInsurancePrice({
        itemPrice: props.product.price,
        isNetherlands: isNL.value,
        isGermany: isDE.value,
        isLu: isLU.value,
        isBe: isBE.value
      }) ?? null
    );

    const currency = getCurrencyCode({ vsfContext });
    const browserDetails = getBrowserDetails({ vsfContext, i18n: context.i18n });

    const fireGtmAddInsurance = () => {
      const insuranceItem = (cartItems.value || []).find(i => i?.is_insurance_item);
      if (!insuranceItem) return;

      const priceNow =
        typeof insuranceItem.price_after_cash_discount === 'number' && insuranceItem.price_after_cash_discount > 0
          ? insuranceItem.price_after_cash_discount
          : insuranceItem.price ?? 0;

      registerEvent(
        eventBuilder('addToCart')({
          products: [
            {
              name: insuranceItem.name,
              id: insuranceItem.sku,
              price: priceNow,
              brand: insuranceItem.brand,
              category: insuranceItem.category,
              quantity: 1
            }
          ],
          vsfContext
        })
      );
    };

    const onToggle = async () => {
      if (isLoading.value) return;
      if (props.cartItemId === null) return;

      const nextState = !isInsured.value;
      optimistic.value = nextState;
      isLoading.value = true;

      try {
        await toggleInsurance({
          cartItemId: props.cartItemId,
          isCurrentlyActive: isInsured.value,
          currency,
          browserDetails
        });

        if (nextState) fireGtmAddInsurance();
      } catch (e) {
        console.error('[Careplan] toggle failed', e);
      } finally {
        isLoading.value = false;
        optimistic.value = null;
      }
    };

    return {
      translate,
      isLoading,
      uiInsuranceState,
      descriptionKey,
      insurancePrice,
      carePlanImage,
      addCurrency,
      priceFormat,
      onToggle
    };
  }
});
